import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import parse from 'html-react-parser'

import ParkingTags from '@/components/parking/ParkingTags'
import { FeatureTypes, Parking } from '@/service'
import PriceTag from '@/components/parkings/PriceTag'
import { useIsMobile } from '@/utils/useMobile'
import { getNavigationIcon } from '@/constants/navigationAssets'

import { useMarket } from '@/utils/multi-markets/context'
import { colors } from '@/styles/theme'

import CommonButton from '@/components/form/Button/CommonButton'

import BtnGlobalCustomStyles from '@/components/form/Button/button.customStyles'

import { useFetchMinimalReviews } from '@/service/apiHooks/useFetchMinimalReviews'

import getFacilities from '@/utils/getFacilities'
import { selectParkingDisplayPrice } from '@/utils/selectParkingDisplayPrice'

import {
  TileContainer,
  ImageContainer,
  InfoContainer,
  PriceAndButton,
  AddressWrapper,
  PriceWrapper,
  StyledImageWrapper,
  ParkingTitle,
  TagsWrapper,
  CrossedText2,
  LowestPriceWrapper,
  CustomisedText2,
  DistanceInfo,
  ParkingTileMobileReview
} from './ParkingTileMobile.styled'
import LastBooking from './ParkingTile/LastBooking'
import RoutingBox from './ParkingTile/Routing'
import {
  FacilityItem,
  FacilityMobileWrapper,
  ParkingRoutingSection
} from './ParkingTile.styled'
import MobileTileRatingContainer from './ParkingTile/MobileTileRatingContainer'

interface ParkingTileProps {
  name: string
  description: string
  imageUrl: string
  onClick: (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
  ) => void
  parking: Parking
  dataTestId: string
}

const ParkingTileMobile: React.FC<ParkingTileProps> = ({
  name,
  description,
  imageUrl,
  onClick,
  parking,
  dataTestId
}) => {
  const router = useRouter()
  const { isMobile } = useIsMobile()
  const { locale } = useMarket()
  const { t } = useTranslation(['parkings', 'common'])
  const { reviewData, isLoading } = useFetchMinimalReviews({
    key: parking.urlSlug,
    locale
  })

  const handleRedirectToParkingPage = () => {
    router.push({
      pathname: '/parkings/[city]/[slug]',
      query: { city: parking.cityUrlSlug, slug: parking.urlSlug }
    })
  }
  const translatedDistanceLabel = t('common:distance.from')?.split(' ')[0]

  const isQuickButtonDisabled =
    parking.subscriptionAvailable === false &&
    parking.reservationAvailable === false

  const isParkAndFly = parking.infoTags?.includes('PARK_AND_FLY')

  const selectedDisplayPrice = selectParkingDisplayPrice(parking.price)

  const shouldHighlightHourlyPrice =
    selectedDisplayPrice?.source === 'lastPricePerHour'

  const shouldDisplayLowestHourlyPrice =
    Boolean(parking.price?.lowestLast30daysPricePerHour) &&
    selectedDisplayPrice?.periodInDays === undefined

  const formattedPrice =
    locale === 'ar'
      ? new Intl.NumberFormat('ar-EG').format(
          parking?.price?.lowestLast30daysPricePerHour?.amount
        )
      : parking?.price?.lowestLast30daysPricePerHour?.amount

  const currencyText = t(
    `common:currency${parking.price?.lowestLast30daysPricePerHour?.currency}` as any
  )
  const formatDistance = (distanceInMeters: number) => {
    625
    if (!distanceInMeters && distanceInMeters !== 0) return null

    const distanceInKm = distanceInMeters / 1000
    return `${distanceInKm
      .toFixed(1)
      .replace('.', ',')} ${translatedDistanceLabel}`
  }

  const distanceText = formatDistance(parking.calculatedDistanceInMeters)

  const facilities = getFacilities(parking?.parkingFeatures ?? {})

  return (
    <TileContainer
      data-test-id={dataTestId}
      onClick={handleRedirectToParkingPage}
    >
      <ImageContainer data-test-id="image-container">
        <StyledImageWrapper data-test-id="image-wrapper">
          <img
            src={imageUrl}
            alt={name}
            width={164}
            height={164}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </StyledImageWrapper>
        {!isLoading && (
          <MobileTileRatingContainer rating={reviewData?.rating} />
        )}
      </ImageContainer>
      <InfoContainer data-test-id="info-container">
        {parking?.lastReservation && (
          <LastBooking value={parking?.lastReservation} />
        )}
        <ParkingTitle data-test-id="parking-title">{name}</ParkingTitle>
        <ParkingTags
          parking={parking}
          short
          positionAbsolute={isMobile}
          noMargin={true}
          isResultLabels={true}
        />
        <ParkingRoutingSection>
          {isParkAndFly ? (
            <>
              {parking?.parkingFeatures?.distanceToAirportInKilometers && (
                <RoutingBox
                  icon="plane"
                  distance={
                    parking?.parkingFeatures?.distanceToAirportInKilometers +
                    'km'
                  }
                  description={''}
                />
              )}
              {parking?.parkingFeatures?.transferTimeInMinutes && (
                <RoutingBox
                  icon="car"
                  distance={
                    parking?.parkingFeatures?.transferTimeInMinutes + 'min'
                  }
                  description={''}
                />
              )}
            </>
          ) : distanceText ? (
            <RoutingBox icon="arrow" distance={distanceText} description={''} />
          ) : (
            <></>
          )}
        </ParkingRoutingSection>
        <FacilityMobileWrapper>
          {facilities?.slice(0, 3)?.map(facility => (
            <FacilityItem seperated={isMobile} key={String(facility)}>
              <>
                <img src={getNavigationIcon('checkTick')} alt="check icon" />
                {t(`parkings:facility.${facility as FeatureTypes}`)}
              </>
            </FacilityItem>
          ))}
        </FacilityMobileWrapper>
        {shouldDisplayLowestHourlyPrice && (
          <LowestPriceWrapper>
            <CustomisedText2 style={{ fontSize: 10 }}>
              {parse(t('common:lastMonthLowestPriceMobile'))}
            </CustomisedText2>
            <CrossedText2>
              {t('common:priceFrom')} {formattedPrice} {currencyText}
            </CrossedText2>
          </LowestPriceWrapper>
        )}
        <PriceAndButton data-test-id="price-and-button">
          {isMobile && selectedDisplayPrice && (
            <TagsWrapper data-test-id="tags-wrapper">
              <PriceWrapper shouldHighlightPrice={shouldHighlightHourlyPrice}>
                <PriceTag
                  price={selectedDisplayPrice.price}
                  isAbsolute={false}
                  noMargin
                  background="transparent"
                  color={
                    shouldHighlightHourlyPrice
                      ? colors.warningRed
                      : colors.twilightBlue
                  }
                  periodInDays={selectedDisplayPrice.periodInDays}
                />
              </PriceWrapper>
            </TagsWrapper>
          )}
        </PriceAndButton>
        <PriceAndButton>
          <CommonButton
            variant="searchWrapper"
            onClick={onClick}
            disabled={isQuickButtonDisabled}
            data-test-id="quick-book-button"
            customStyle={[
              BtnGlobalCustomStyles.treriary,
              BtnGlobalCustomStyles.treriaryLarge,
              isQuickButtonDisabled &&
                BtnGlobalCustomStyles.disabledQuickBooking
            ]}
          >
            {t('parkings:tile.button.book')}
          </CommonButton>
        </PriceAndButton>
      </InfoContainer>
    </TileContainer>
  )
}

export default ParkingTileMobile
